import React, { useState, useEffect, useCallback, useMemo } from 'react';
import authedFetch from '../lib/authedFetch';
import {
  ArrowsClockwise, PaperPlaneTilt, MagnifyingGlass, Check, EnvelopeSimple, Star, CaretRight, Plus,
  ArrowRight, CheckCircle, WarningCircle, Warning,
} from '@phosphor-icons/react';

// ------------------------------------------------------------------ //
// Escopo (.pr-*) sobre o Care Design System v3 e o kit do CareOS
// (src/styles/care-ui.css): botão, campo, aviso, pílula, vazio, seção
// e esqueleto vêm do kit (.ui-*). Aqui fica só o que é desta tela — a
// grade da linha e o trilho de posse. Só papéis semânticos, nenhum hex.
// Acento único = clay; teal, rust e amber são ESTADO. Hover por overlay
// (nunca troca de matiz) e foco pelo anel global :focus-visible.
// ------------------------------------------------------------------ //
const PR_CSS = `
.pr-root{gap:0;font-family:var(--care-font-sans);color:var(--care-text-primary);}
.pr-num{font-variant-numeric:tabular-nums lining-nums;}
.pr-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;}

/* aviso em linha (.ui-aviso) */
.pr-aviso{margin-bottom:var(--care-space-6);}

/* resumo da pauta — texto, não um arco-íris de chips */
.pr-tally{display:flex;align-items:center;gap:var(--care-space-2);flex-wrap:wrap;padding:var(--care-space-5) 0;margin-bottom:var(--care-space-2);
  border-top:var(--care-border-hairline) solid var(--care-border-subtle);border-bottom:var(--care-border-hairline) solid var(--care-border-subtle);}
.pr-tally b{font-weight:var(--care-weight-semibold);}

/* filtros */
.pr-filters{display:flex;gap:var(--care-space-4);margin:var(--care-space-6) 0 var(--care-space-2);flex-wrap:wrap;}
.pr-search{position:relative;flex:1;min-width:200px;}
.pr-search svg{position:absolute;left:var(--care-space-5);top:50%;transform:translateY(-50%);color:var(--care-text-tertiary);pointer-events:none;}
.pr-search .ui-campo{width:100%;padding-left:var(--care-space-10);}

/* seções da pauta */
.pr-sec{margin-top:var(--care-space-8);}

/* linha */
.pr-row{display:grid;grid-template-columns:20px minmax(170px,1.05fr) minmax(230px,1.25fr) 160px;
  gap:var(--care-space-6);align-items:start;padding:var(--care-space-6) var(--care-space-5);border-radius:var(--radius);
  border:var(--care-border-hairline) solid transparent;
  transition:background-color var(--care-duration-fast) var(--care-ease-standard),border-color var(--care-duration-fast) var(--care-ease-standard);}
.pr-row + .pr-row{border-top:var(--care-border-hairline) solid var(--care-border-subtle);border-top-left-radius:0;border-top-right-radius:0;}
.pr-row[data-selecionada]{background:var(--care-state-selected-bg);}
.pr-row[data-selecionada] .pr-name{color:var(--ai-accent-text);}
.pr-row-dim .pr-name{color:var(--care-text-secondary);}
.pr-check-hit{display:flex;align-items:center;justify-content:center;width:32px;height:32px;margin:calc(-1 * var(--care-space-3));cursor:pointer;}
.pr-check{width:20px;height:20px;accent-color:var(--care-accent-solid);cursor:pointer;margin:0;}

/* identidade */
.pr-who{min-width:0;display:flex;flex-direction:column;gap:var(--care-space-2);}
.pr-name{font-size:var(--care-text-base);font-weight:var(--care-weight-semibold);min-height:32px;color:var(--care-text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pr-name-none{font-weight:var(--care-weight-medium);color:var(--care-text-secondary);}
.pr-nome-btn{background:none;border:0;padding:0 var(--care-space-2);margin-left:calc(-1 * var(--care-space-2));border-radius:var(--radius);
  font-family:inherit;text-align:left;cursor:text;
  transition:background-color var(--care-duration-fast) var(--care-ease-standard);}
.pr-nome-btn:hover{background:var(--care-state-hover-overlay);}
.pr-nome-btn:active{background:var(--care-state-pressed-overlay);}
.pr-who .ui-campo{width:100%;font-size:var(--care-text-base);font-weight:var(--care-weight-semibold);}
.pr-mail{font-size:var(--care-text-xs);color:var(--care-text-tertiary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pr-when{font-size:var(--care-text-xs);color:var(--care-text-tertiary);}
.pr-stamp{display:inline-flex;align-items:center;gap:var(--care-space-2);font-size:var(--care-text-xs);color:var(--care-positive-text);margin-top:var(--care-space-1);}
/* ação de texto (botão fantasma): o rótulo alinha com o texto de cima */
.pr-recuo{align-self:flex-start;margin-left:calc(-1 * var(--care-space-4));}

/* responsável */
.pr-stars{display:flex;flex-wrap:wrap;align-items:center;gap:var(--care-space-2);margin-top:var(--care-space-2);}

/* ── ASSINATURA: o trilho de posse ── */
.pr-lanes{display:flex;flex-direction:column;gap:var(--care-space-2);min-width:0;}
.pr-lane{display:grid;grid-template-columns:72px auto 1fr;align-items:center;gap:var(--care-space-4);}
.pr-lane-l{font-size:var(--care-text-xs);color:var(--care-text-secondary);font-weight:var(--care-weight-medium);}
.pr-track{display:flex;align-items:center;}
.pr-node{position:relative;width:24px;height:24px;border-radius:var(--care-radius-full);
  border:var(--care-border-thick) solid var(--care-border-interactive);background:var(--care-bg-surface);
  color:var(--care-accent-on-solid);cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;
  transition:background-color var(--care-duration-fast) var(--care-ease-standard),border-color var(--care-duration-fast) var(--care-ease-standard);}
.pr-node::after{content:"";position:absolute;inset:-4px;}
.pr-node:hover:not(:disabled){border-color:var(--care-border-strong);background:var(--care-state-hover-overlay);}
.pr-node:active:not(:disabled){background:var(--care-state-pressed-overlay);}
.pr-node:disabled{cursor:not-allowed;opacity:var(--care-state-disabled-opacity);}
.pr-node-on{background:var(--ai-accent-solid);border-color:var(--ai-accent-solid);}
.pr-node-on:hover:not(:disabled),.pr-node-on:active:not(:disabled){background:var(--care-accent-solid-hover);border-color:var(--care-accent-solid-hover);}
.pr-node-done{background:var(--care-positive-text);border-color:var(--care-positive-text);}
.pr-node-done:hover:not(:disabled){background:var(--care-positive-text);border-color:var(--care-positive-text);
  background-image:linear-gradient(var(--care-state-hover-overlay),var(--care-state-hover-overlay));}
.pr-node-done:active:not(:disabled){background:var(--care-positive-text);border-color:var(--care-positive-text);
  background-image:linear-gradient(var(--care-state-pressed-overlay),var(--care-state-pressed-overlay));}
.pr-wire{width:24px;height:2px;background:var(--care-border-default);transition:background-color var(--care-duration-fast) var(--care-ease-standard);}
.pr-wire-on{background:var(--ai-accent);}
.pr-wire-done{background:var(--care-positive-text);}
.pr-posse{font-size:var(--care-text-xs);line-height:var(--care-leading-snug);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.pr-posse-consultor{color:var(--care-accent-text);font-weight:var(--care-weight-semibold);}
.pr-posse-cliente{color:var(--care-text-secondary);}
.pr-posse-tarde{color:var(--care-warning-text);font-weight:var(--care-weight-semibold);}
.pr-posse-fechado{color:var(--care-positive-text);}

/* ação */
.pr-act{display:flex;flex-direction:column;align-items:flex-start;gap:var(--care-space-2);}
.pr-act-hint{font-size:var(--care-text-xs);color:var(--care-text-tertiary);}

/* carregando (esqueleto .skel do kit) + pauta limpa + arquivo */
.pr-skel-row{display:grid;grid-template-columns:20px minmax(170px,1.05fr) minmax(230px,1.25fr) 160px;gap:var(--care-space-6);align-items:start;padding:var(--care-space-6) var(--care-space-5);}
.pr-skel-row + .pr-skel-row{border-top:var(--care-border-hairline) solid var(--care-border-subtle);}
.pr-skel-col{display:flex;flex-direction:column;gap:var(--care-space-4);}
.pr-limpa{margin-top:var(--care-space-8);}
.pr-arch{margin-top:var(--care-space-9);padding:var(--care-space-6) var(--care-space-7);background:var(--care-bg-surface-sunken);
  border:var(--care-border-hairline) solid var(--care-border-subtle);border-radius:var(--radius-card);}
.pr-arch .ui-secao__chevron{transition:transform var(--care-duration-fast) var(--care-ease-standard);}
.pr-arch [aria-expanded="true"] .ui-secao__chevron{transform:rotate(90deg);}
.pr-arch-d{font-size:var(--care-text-sm);letter-spacing:var(--care-tracking-caption);color:var(--care-text-secondary);
  margin:var(--care-space-4) 0 0 calc(var(--care-space-2) + 16px + var(--care-space-3));line-height:var(--care-leading-normal);max-width:64ch;}

@media (max-width:860px){
  .pr-row,.pr-skel-row{grid-template-columns:20px 1fr;gap:var(--care-space-5);}
  .pr-lanes,.pr-act{grid-column:2;}
  .pr-act{align-items:stretch;}
}
@media (prefers-reduced-motion:reduce){
  .pr-root *{transition-duration:0.01ms !important;}
}
`;

// Reunião com mais de 90 dias: o documento não volta mais. Sai da pauta.
const ARQUIVO_DIAS = 90;
const SLA_DIAS = 2;

// Sempre horário de Brasília, independente do fuso do navegador de quem abre.
const BRT = 'America/Sao_Paulo';

// Formatadores criados UMA vez no módulo (criar Intl.DateTimeFormat por chamada
// custa caro e roda por linha a cada render/tecla na busca).
const FMT_DATA_BRT = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: BRT });
const FMT_YMD_BRT = new Intl.DateTimeFormat('en-CA', { timeZone: BRT });

const formatData = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  // Intl.format lança RangeError em data inválida (o toLocale* devolvia "Invalid Date").
  if (Number.isNaN(d.getTime())) return '—';
  return FMT_DATA_BRT.format(d);
};

// Dias corridos no calendário de Brasília (não fração de 24h no fuso do navegador).
const ymdBRT = (dt) => FMT_YMD_BRT.format(dt).split('-').map(Number);
const diasDesde = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const [y1, m1, d1] = ymdBRT(d);
  const [y2, m2, d2] = ymdBRT(new Date());
  return Math.max(0, Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000));
};

// De quem é a bola, num trilho. É a pergunta que o operador faz o dia inteiro:
// ele nunca fala com o cliente — cobra o consultor, que cobra o cliente.
function posse(pedido, recebido) {
  if (recebido) return 'fechado';
  if (!pedido) return 'consultor';   // o consultor ainda nem pediu ao cliente
  return 'cliente';                  // pediu; agora depende do cliente entregar
}

// Estado da linha, derivado dos DOIS trilhos separadamente. Isto corrige, na
// leitura, o furo do backend (`pediu = admin OR aderencia`): pedir só o Admin
// não esconde mais a cobrança da Aderência — a raia dela continua acesa.
function estado(l) {
  const a = posse(l.docs_admin_pedidos, l.docs_admin_recebidos);
  const d = posse(l.docs_aderencia_pedidos, l.docs_aderencia_recebidos);
  if (a === 'fechado' && d === 'fechado') return 'completo';
  if (l.precisa_responsavel) return 'sem_responsavel';
  if (a === 'consultor' || d === 'consultor') return 'a_cobrar';
  const parado = diasDesde(l.docs_status_atualizado_em || l.reuniao_em);
  return parado > SLA_DIAS ? 'a_cobrar' : 'aguardando';
}

const SECOES = [
  { key: 'a_cobrar', titulo: 'A cobrar',
    desc: 'A bola está com o consultor — ou o cliente passou do prazo. Um clique manda o e-mail só pro responsável.' },
  { key: 'sem_responsavel', titulo: 'Sem responsável',
    desc: 'Ninguém pra cobrar. Marque como responsável o consultor que conduz o contato e a linha vira cobrança.' },
  { key: 'aguardando', titulo: 'Aguardando cliente',
    desc: 'O consultor já pediu e o prazo ainda corre. Nada a fazer — a não ser que você queira lembrar.' },
  { key: 'completo', titulo: 'Concluído agora',
    desc: 'Some da lista no próximo carregamento. Até lá dá pra desmarcar, se o clique foi engano.' },
];

export default function PostReuniaoTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroCarga, setErroCarga] = useState(false);  // distingue "pauta vazia" de "falha ao carregar"
  const [filtroConsultor, setFiltroConsultor] = useState('todos');
  const [foco, setFoco] = useState(null);          // seção isolada pelo resumo
  const [search, setSearch] = useState('');
  const [pendingDigest, setPendingDigest] = useState(false);
  const [msg, setMsg] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [consultoresAtivos, setConsultoresAtivos] = useState([]);
  const [enviandoId, setEnviandoId] = useState(null);
  const [verArquivo, setVerArquivo] = useState(false);
  // Só para o rótulo do botão: "Sincronizando…" quando o sync foi disparado (não na carga inicial).
  const [sincronizando, setSincronizando] = useState(false);
  // Linhas mexidas agora: marcar um doc muda o estado, o que faria a linha pular
  // de seção debaixo do cursor. Ficam onde estão até o próximo carregamento.
  const [recentes, setRecentes] = useState(() => new Set());

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    (async () => {
      try {
        // authedFetch como no resto do arquivo (o endpoint é público, mas com auth também funciona).
        const r = await authedFetch(`${backendUrl}/api/consultores-ativos`);
        if (r.ok) {
          const d = await r.json();
          setConsultoresAtivos(d.consultores || []);
        }
      } catch (e) {
        console.warn('Falha ao buscar consultores ativos:', e);
      }
    })();
  }, [backendUrl]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authedFetch(`${backendUrl}/api/docs/pendentes`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setLeads(d.leads || []);
      setRecentes(new Set());
      setErroCarga(false);
    } catch (e) {
      // Sem isso, uma falha de rede mostrava "Nada na pauta" — enganoso numa tela de cobrança.
      setErroCarga(true);
      setMsg({ type: 'error', text: `Não consegui carregar a pauta (${e?.message || e}). Tente de novo.` });
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => { carregar(); }, [carregar]);

  const marcarRecente = useCallback((id) => {
    setRecentes(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const aplicarLead = useCallback((novo) => {
    if (!novo || !novo.id) return;
    setLeads(prev => prev.map(l => (l.id === novo.id ? { ...l, ...novo } : l)));
  }, []);

  // PATCH otimista: pinta na hora, confirma com a linha que o servidor devolve,
  // desfaz e avisa se ele recusar.
  const patchLead = useCallback(async (id, body, rollback, erroMsg) => {
    marcarRecente(id);
    try {
      const r = await authedFetch(`${backendUrl}/api/docs/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json().catch(() => null);
      aplicarLead(d?.lead);
      return true;
    } catch {
      setLeads(prev => prev.map(l => (l.id === id ? { ...l, ...rollback } : l)));
      setMsg({ type: 'error', text: erroMsg });
      return false;
    }
  }, [backendUrl, aplicarLead, marcarRecente]);

  const togglar = async (id, campo, valor) => {
    setLeads(prev => prev.map(l => (l.id === id ? { ...l, [campo]: valor } : l)));
    await patchLead(id, { [campo]: valor }, { [campo]: !valor }, 'Não consegui salvar essa marcação. Tente de novo.');
  };

  const salvarNome = async (id, novoNome) => {
    const limpo = (novoNome || '').trim();
    const antes = leads.find(l => l.id === id)?.nome ?? null;
    setLeads(prev => prev.map(l => (l.id === id ? { ...l, nome: limpo || null } : l)));
    await patchLead(id, { nome: limpo }, { nome: antes }, 'Não consegui salvar o nome. Tente de novo.');
  };

  const setResponsavel = async (id, consultorNome) => {
    const atual = leads.find(l => l.id === id);
    const antes = atual?.consultor_responsavel ?? null;
    const novo = antes === consultorNome ? null : consultorNome;   // clicar de novo remove
    setLeads(prev => prev.map(l => (l.id === id ? { ...l, consultor_responsavel: novo } : l)));
    await patchLead(id, { consultor_responsavel: novo }, { consultor_responsavel: antes },
      'Não consegui salvar o responsável. Tente de novo.');
  };

  const toggleSelected = useCallback((id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const sincronizar = async () => {
    setLoading(true);
    setSincronizando(true);
    setMsg(null);
    try {
      const r1 = await authedFetch(`${backendUrl}/api/reunioes/sync?max_pages=2`, { method: 'POST' });
      const d1 = await r1.json().catch(() => null);
      if (!r1.ok) {
        setMsg({ type: 'error', text: `Falha no sync tl;dv (${r1.status}): ${d1?.detail || 'sem detalhes'}` });
        return;
      }
      const r2 = await authedFetch(`${backendUrl}/api/docs/sync-reunioes?recent_days=60`, { method: 'POST' });
      const d2 = await r2.json().catch(() => null);
      if (!r2.ok) {
        setMsg({ type: 'error', text: `Falha no sync docs (${r2.status}): ${d2?.detail || 'sem detalhes'}` });
        return;
      }
      setMsg({
        type: 'success',
        text: `tl;dv: ${d1?.new ?? 0} novas, ${d1?.updated ?? 0} atualizadas · docs: ${d2?.inseridos ?? 0} novos, ${d2?.atualizados ?? 0} atualizados`,
      });
      await carregar();
    } catch (e) {
      setMsg({ type: 'error', text: `Erro de rede: ${e?.message || e}` });
    } finally {
      setLoading(false);
      setSincronizando(false);
    }
  };

  const enviarCobranca = async (ids, descricao) => {
    const r = await authedFetch(`${backendUrl}/api/docs/digest/run-selected`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_ids: ids }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) {
      setMsg({ type: 'error', text: `Falha: ${d?.detail || r.status}` });
      return false;
    }
    const semResp = Array.isArray(d.sem_responsavel) ? d.sem_responsavel : [];
    if (d.enviados > 0) {
      let text = descricao(d);
      if (semResp.length > 0) {
        text += ` Atenção: ${semResp.length} sem responsável, não enviados: ${semResp.slice(0, 4).join(', ')}${semResp.length > 4 ? '…' : ''}. Defina o responsável.`;
      }
      // Os e-mails saíram: contatos sem responsável são ATENÇÃO (amber), não falha.
      setMsg({ type: semResp.length > 0 ? 'warning' : 'success', text });
    } else {
      setMsg({ type: 'error', text: 'Nada enviado — responsável sem e-mail cadastrado nos consultores.' });
    }
    await carregar();
    return true;
  };

  const cobrarSelecionados = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Enviar e-mail ao consultor responsável de ${ids.length} contato(s)? Pode reenviar quantas vezes quiser — é lembrete.`)) return;
    setPendingDigest(true);
    setMsg(null);
    try {
      const ok = await enviarCobranca(ids, (d) =>
        `${d.enviados} e-mail(s) enviado(s) · ${d.cobrados ?? 0} contato(s) marcado(s) como cobrado(s).`);
      if (ok) setSelected(new Set());
    } catch (e) {
      setMsg({ type: 'error', text: `Erro: ${e.message}` });
    } finally {
      setPendingDigest(false);
    }
  };

  const cobrarUm = async (lead) => {
    const resp = lead.responsavel_efetivo;
    if (!resp) return;
    if (!window.confirm(`Enviar e-mail para ${resp} cobrando os documentos de ${lead.nome || lead.email}? Pode reenviar como lembrete.`)) return;
    setEnviandoId(lead.id);
    setMsg(null);
    try {
      await enviarCobranca([lead.id], () =>
        `E-mail enviado a ${resp} · ${lead.nome || lead.email} marcado como cobrado.`);
    } catch (e) {
      setMsg({ type: 'error', text: `Erro: ${e.message}` });
    } finally {
      setEnviandoId(null);
    }
  };

  const consultores = useMemo(
    () => consultoresAtivos.map(c => c.nome).sort((a, b) => a.localeCompare(b)),
    [consultoresAtivos]
  );

  // Pauta = trabalho vivo. Arquivo = reunião de +90 dias: o documento não volta.
  const { pauta, arquivo } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const visivel = (l) =>
      (filtroConsultor === 'todos' || (l.consultores || []).includes(filtroConsultor))
      && (!q || (l.nome || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q));

    const p = [], a = [];
    for (const l of leads) {
      if (!visivel(l)) continue;
      const velha = (diasDesde(l.reuniao_em) ?? 0) > ARQUIVO_DIAS;
      // uma linha já mexida nesta sessão nunca cai no arquivo debaixo do cursor
      (velha && !recentes.has(l.id) ? a : p).push(l);
    }
    return { pauta: p, arquivo: a };
  }, [leads, filtroConsultor, search, recentes]);

  // Agrupa a pauta por estado; dentro de cada seção, a reunião MAIS RECENTE primeiro.
  // (A cobrança quente é a de ontem — a de 80 dias atrás praticamente não volta.)
  const grupos = useMemo(() => {
    const g = { a_cobrar: [], sem_responsavel: [], aguardando: [], completo: [] };
    for (const l of pauta) {
      const e = estado(l);
      if (g[e]) g[e].push(l);
    }
    for (const k of Object.keys(g)) {
      g[k].sort((x, y) => new Date(y.reuniao_em) - new Date(x.reuniao_em));
    }
    return g;
  }, [pauta]);

  const nadaNaPauta = pauta.length === 0;
  const semAcao = grupos.a_cobrar.length === 0 && grupos.sem_responsavel.length === 0;

  const arquivoNadaPedido = useMemo(
    () => arquivo.filter(l => !l.docs_admin_pedidos && !l.docs_aderencia_pedidos).length,
    [arquivo]
  );

  const secoesVisiveis = SECOES.filter(s => grupos[s.key].length > 0 && (!foco || foco === s.key));

  const linhaProps = (l) => ({
    lead: l,
    selected: selected.has(l.id),
    onToggleSelected: toggleSelected,
    togglar,
    salvarNome,
    setResponsavel,
    enviando: enviandoId === l.id,
    onCobrar: cobrarUm,
  });

  return (
    <div className="ui-card pr-root">
      <style>{PR_CSS}</style>

      <header className="ui-cabecalho" style={{ marginBottom: 'var(--care-space-7)' }}>
        <div>
          <h2 className="ui-titulo" data-tamanho="lg">Pós-Reunião</h2>
          <p className="ui-paragrafo" style={{ maxWidth: '64ch', marginTop: 'var(--care-space-2)' }}>
            Depois da Apresentação, cada contato deve dois documentos: <b>Admin</b> e <b>Aderência</b>.
            Você não cobra o cliente — cobra o consultor responsável, e ele cobra o cliente.
          </p>
        </div>
        <div className="ui-cabecalho__acoes">
          <button
            onClick={sincronizar}
            disabled={loading}
            aria-busy={loading}
            className="ui-botao"
            data-variante="fio"
            title="Buscar reuniões novas no tl;dv"
          >
            <ArrowsClockwise size={16} weight="bold" aria-hidden="true" />
            {sincronizando ? 'Sincronizando…' : 'Sincronizar'}
          </button>
          <button
            onClick={cobrarSelecionados}
            disabled={pendingDigest || selected.size === 0}
            className="ui-botao"
            data-variante="solido"
            title="Um e-mail por consultor responsável, só com os contatos selecionados"
          >
            <PaperPlaneTilt size={16} weight="bold" aria-hidden="true" />
            {pendingDigest ? 'Enviando…' : `Cobrar selecionados${selected.size ? ` (${selected.size})` : ''}`}
          </button>
        </div>
      </header>

      {msg && (
        <div
          role={msg.type === 'error' ? 'alert' : 'status'}
          className="ui-aviso pr-aviso"
          data-tom={msg.type === 'error' ? 'negativo' : msg.type === 'warning' ? 'atencao' : 'positivo'}
        >
          {msg.type === 'error'
            ? <WarningCircle size={16} weight="bold" aria-hidden="true" />
            : msg.type === 'warning'
              ? <Warning size={16} weight="bold" aria-hidden="true" />
              : <CheckCircle size={16} weight="bold" aria-hidden="true" />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="pr-tally">
        {SECOES.filter(s => s.key !== 'completo').map((s, i) => (
          <React.Fragment key={s.key}>
            {i > 0 && <span aria-hidden="true" style={{ color: 'var(--care-text-tertiary)' }}>·</span>}
            <button
              className="ui-botao"
              data-variante="fantasma"
              data-tamanho="sm"
              aria-pressed={foco === s.key}
              onClick={() => setFoco(foco === s.key ? null : s.key)}
              title={foco === s.key ? 'Mostrar tudo' : `Ver só "${s.titulo}"`}
            >
              <b className="pr-num">{grupos[s.key].length}</b> {s.titulo.toLowerCase()}
            </button>
          </React.Fragment>
        ))}
      </div>

      <div className="pr-filters">
        <div className="pr-search">
          <MagnifyingGlass size={16} weight="bold" aria-hidden="true" />
          <input
            className="ui-campo"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail…"
            aria-label="Buscar por nome ou e-mail"
          />
        </div>
        <select className="ui-campo" aria-label="Filtrar por consultor" value={filtroConsultor} onChange={e => setFiltroConsultor(e.target.value)}>
          <option value="todos">Todos os consultores</option>
          {consultores.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div role="status" aria-busy="true">
          <span className="pr-sr">Carregando a pauta…</span>
          {[0, 1, 2].map(i => (
            <div className="pr-skel-row" key={i} aria-hidden="true">
              <span className="skel" style={{ display: 'block', width: 20, height: 20 }} />
              <div className="pr-skel-col">
                <span className="skel" style={{ display: 'block', width: '70%', height: 20 }} />
                <span className="skel" style={{ display: 'block', width: '90%', height: 12 }} />
                <span className="skel" style={{ display: 'block', width: '50%', height: 12 }} />
              </div>
              <div className="pr-skel-col">
                <span className="skel" style={{ display: 'block', width: '85%', height: 24 }} />
                <span className="skel" style={{ display: 'block', width: '85%', height: 24 }} />
              </div>
              <span className="skel" style={{ display: 'block', width: 128, height: 32 }} />
            </div>
          ))}
        </div>
      ) : erroCarga ? (
        // Falha de rede/servidor NÃO é pauta vazia — dizer "Nada na pauta" aqui enganaria o operador.
        <div className="ui-vazio-bloco" data-area>
          <p className="ui-vazio">Não deu pra carregar a pauta.</p>
          <p className="ui-vazio__dica">Falha de rede ou do servidor.</p>
          <button className="ui-vazio__caminho" onClick={carregar}>
            Tentar de novo <ArrowRight size={16} weight="bold" aria-hidden="true" />
          </button>
        </div>
      ) : nadaNaPauta ? (
        <div className="ui-vazio-bloco" data-area>
          <p className="ui-vazio">Nada na pauta.</p>
          <p className="ui-vazio__dica">
            {search || filtroConsultor !== 'todos'
              ? 'Nenhum contato bate com esse filtro.'
              : 'Nenhuma cobrança pendente de reunião recente.'}
          </p>
        </div>
      ) : (
        <>
          {semAcao && !foco && (
            <div className="ui-vazio-bloco pr-limpa">
              <p className="ui-vazio">Pauta limpa.</p>
              <p className="ui-vazio__dica">Nada a cobrar agora — o que resta está com o cliente, dentro do prazo.</p>
            </div>
          )}

          {secoesVisiveis.map(s => (
            <section className="pr-sec" key={s.key}>
              <div className="ui-trilho" data-gap="5" data-nowrap>
                <h3 className="ui-rotulo" data-tom="forte" style={{ margin: 0 }}>{s.titulo}</h3>
                <span className="ui-meta pr-num">{grupos[s.key].length}</span>
                <hr className="ui-fio" style={{ flex: 1 }} />
              </div>
              <p className="ui-paragrafo" style={{ margin: 'var(--care-space-1) 0 var(--care-space-4)' }}>{s.desc}</p>
              {grupos[s.key].map(l => <Linha key={l.id} {...linhaProps(l)} />)}
            </section>
          ))}
        </>
      )}

      {arquivo.length > 0 && (
        <div className="pr-arch">
          <button
            className="ui-secao__cabecalho"
            aria-expanded={verArquivo}
            aria-controls="pr-arquivo"
            onClick={() => setVerArquivo(v => !v)}
          >
            <span className="ui-secao__chevron"><CaretRight size={16} weight="bold" aria-hidden="true" /></span>
            <span className="ui-rotulo">
              Arquivo — <span className="pr-num">{arquivo.length}</span> de reuniões com mais de {ARQUIVO_DIAS} dias
            </span>
          </button>
          {!verArquivo && (
            <p className="pr-arch-d">
              {arquivoNadaPedido === arquivo.length
                ? 'Em nenhuma delas o documento chegou a ser pedido. Documento de reunião tão antiga não costuma voltar — por isso ficam fora da pauta.'
                : `Em ${arquivoNadaPedido} delas o documento nunca chegou a ser pedido. Ficam fora da pauta pra não enterrar o trabalho vivo.`}
            </p>
          )}
          {verArquivo && (
            <div id="pr-arquivo" style={{ marginTop: 'var(--care-space-5)' }}>
              {arquivo
                .slice()
                .sort((a, b) => new Date(b.reuniao_em) - new Date(a.reuniao_em))
                .map(l => <Linha key={l.id} {...linhaProps(l)} dim />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────── a linha ───────────────────────────

function Linha({ lead, selected, onToggleSelected, togglar, salvarNome, setResponsavel, enviando, onCobrar, dim }) {
  const d = diasDesde(lead.reuniao_em);
  const e = estado(lead);
  const resp = lead.responsavel_efetivo;
  const semNome = !(lead.nome || '').trim();

  return (
    <div className={`pr-row${dim ? ' pr-row-dim' : ''}`} data-selecionada={selected || undefined}>
      <label className="pr-check-hit">
        <input
          type="checkbox"
          className="pr-check"
          checked={!!selected}
          onChange={() => onToggleSelected(lead.id)}
          aria-label={`Selecionar ${lead.nome || lead.email}`}
        />
      </label>

      {/* quem */}
      <div className="pr-who">
        <Identidade lead={lead} semNome={semNome} onSave={(novo) => salvarNome(lead.id, novo)} />
        {!semNome && <span className="pr-mail">{lead.email || '—'}</span>}
        <span className="pr-when pr-num">
          reunião {formatData(lead.reuniao_em)}{d != null && ` · há ${d}d`}
        </span>
        {lead.cobrado_em && (
          <span className="pr-stamp pr-num">
            <Check size={16} weight="bold" aria-hidden="true" /> cobrado {formatData(lead.cobrado_em)}{lead.cobrado_count > 1 ? ` · ${lead.cobrado_count}x` : ''}
          </span>
        )}
        <div className="pr-stars">
          {(lead.consultores || []).map(c => {
            const on = lead.consultor_responsavel === c;
            return (
              <button
                key={c}
                className="ui-botao"
                data-variante="fio"
                data-tamanho="sm"
                aria-pressed={on}
                onClick={() => setResponsavel(lead.id, c)}
                title={on
                  ? 'Responsável pela cobrança — clique pra remover'
                  : 'Marcar como responsável pela cobrança'}
              >
                {on && <Star size={16} weight="fill" aria-hidden="true" />}{c}
              </button>
            );
          })}
          {lead.precisa_responsavel && (
            <span className="ui-pilula" data-tom="atencao" title="Sem responsável claro — o e-mail não tem pra quem ir">
              {(lead.consultores || []).length ? 'defina o responsável' : 'sem consultor'}
            </span>
          )}
        </div>
      </div>

      {/* ASSINATURA: de quem é a bola, em cada documento */}
      <div className="pr-lanes">
        <Trilho
          label="Admin"
          pedido={lead.docs_admin_pedidos}
          recebido={lead.docs_admin_recebidos}
          desdeReuniao={d}
          desdeMudanca={diasDesde(lead.docs_status_atualizado_em || lead.reuniao_em)}
          onPedido={(v) => togglar(lead.id, 'docs_admin_pedidos', v)}
          onRecebido={(v) => togglar(lead.id, 'docs_admin_recebidos', v)}
        />
        <Trilho
          label="Aderência"
          pedido={lead.docs_aderencia_pedidos}
          recebido={lead.docs_aderencia_recebidos}
          desdeReuniao={d}
          desdeMudanca={diasDesde(lead.docs_status_atualizado_em || lead.reuniao_em)}
          onPedido={(v) => togglar(lead.id, 'docs_aderencia_pedidos', v)}
          onRecebido={(v) => togglar(lead.id, 'docs_aderencia_recebidos', v)}
        />
      </div>

      {/* a única ação desta linha */}
      <div className="pr-act">
        {e === 'completo' ? (
          <span className="ui-pilula" data-tom="positivo"><Check size={12} weight="bold" aria-hidden="true" /> Concluído</span>
        ) : e === 'sem_responsavel' ? (
          <span className="ui-trilho pr-act-hint" data-gap="4">
            <Star size={16} weight="bold" aria-hidden="true" /> marque o responsável ao lado
          </span>
        ) : e === 'a_cobrar' ? (
          <>
            <button className="ui-botao" data-variante="solido" data-tamanho="sm" disabled={enviando} onClick={() => onCobrar(lead)}>
              <EnvelopeSimple size={16} weight="bold" aria-hidden="true" /> {enviando ? 'Enviando…' : `Cobrar ${primeiroNome(resp)}`}
            </button>
            <span className="pr-act-hint">e-mail para {resp}</span>
          </>
        ) : (
          <>
            <span className="pr-act-hint">no prazo do cliente</span>
            {resp && (
              <button className="ui-botao pr-recuo" data-variante="fantasma" data-tamanho="sm" disabled={enviando} onClick={() => onCobrar(lead)}>
                {enviando ? 'enviando…' : 'lembrar mesmo assim'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const primeiroNome = (n) => (n || '').trim().split(/\s+/)[0] || '';

// Um trilho: dois nós ligados por um fio, e — o que importa — de quem é a bola.
function Trilho({ label, pedido, recebido, desdeReuniao, desdeMudanca, onPedido, onRecebido }) {
  const p = posse(pedido, recebido);
  const tarde = p === 'cliente' && desdeMudanca > SLA_DIAS;

  let texto, cls;
  if (p === 'fechado') { texto = 'recebido'; cls = 'pr-posse-fechado'; }
  else if (p === 'consultor') {
    texto = `com o consultor${desdeReuniao != null ? ` · ${desdeReuniao}d` : ''}`;
    cls = 'pr-posse-consultor';
  } else {
    texto = `com o cliente${desdeMudanca != null ? ` · ${desdeMudanca}d` : ''}`;
    if (tarde) texto += ' · atrasado';
    cls = tarde ? 'pr-posse-tarde' : 'pr-posse-cliente';
  }

  const wire = recebido ? 'pr-wire pr-wire-done' : pedido ? 'pr-wire pr-wire-on' : 'pr-wire';

  return (
    <div className="pr-lane">
      <span className="pr-lane-l">{label}</span>
      <span className="pr-track">
        <button
          className={`pr-node${pedido ? ' pr-node-on' : ''}`}
          aria-pressed={!!pedido}
          aria-label={`${label}: pedido ao cliente`}
          onClick={() => onPedido(!pedido)}
          title={pedido ? 'Pedido ao cliente — clique pra desmarcar' : 'Marcar que o documento foi pedido'}
        >
          {pedido && <Check size={16} weight="bold" aria-hidden="true" />}
        </button>
        <span className={wire} />
        <button
          className={`pr-node${recebido ? ' pr-node-done' : ''}`}
          disabled={!pedido}
          aria-pressed={!!recebido}
          aria-label={`${label}: documento recebido`}
          onClick={() => onRecebido(!recebido)}
          title={recebido ? 'Recebido — clique pra desmarcar' : (pedido ? 'Marcar como recebido' : 'Peça primeiro')}
        >
          {recebido && <Check size={16} weight="bold" aria-hidden="true" />}
        </button>
      </span>
      <span className={`pr-posse pr-num ${cls}`}>{texto}</span>
    </div>
  );
}

// A identidade é o e-mail quando não há nome — e há 156 contatos sem nome.
// Melhor mostrar o que existe do que um placeholder pedindo trabalho manual.
function Identidade({ lead, semNome, onSave }) {
  const [editando, setEditando] = useState(false);
  const [draft, setDraft] = useState(lead.nome || '');
  useEffect(() => { setDraft(lead.nome || ''); }, [lead.nome]);

  const finalizar = () => {
    setEditando(false);
    if ((draft || '').trim() !== (lead.nome || '').trim()) onSave(draft);
  };

  if (editando) {
    return (
      <input
        autoFocus
        className="ui-campo"
        data-tamanho="sm"
        aria-label="Nome do contato"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={finalizar}
        onKeyDown={(e) => {
          if (e.key === 'Enter') finalizar();
          if (e.key === 'Escape') { setDraft(lead.nome || ''); setEditando(false); }
        }}
        placeholder="Nome do contato"
      />
    );
  }

  if (semNome) {
    return (
      <>
        <span className="pr-name pr-name-none" title={lead.email || ''}>{lead.email || 'sem identificação'}</span>
        <button className="ui-botao pr-recuo" data-variante="fantasma" data-tamanho="sm" onClick={() => setEditando(true)}>
          <Plus size={16} weight="bold" aria-hidden="true" /> dar um nome
        </button>
      </>
    );
  }

  return (
    <button className="pr-name pr-nome-btn"
      onClick={() => setEditando(true)} title="Clique para editar o nome">
      {lead.nome}
    </button>
  );
}
